/**
 * DebtCardInput Component
 * Mobile-friendly card-based input for debt breakdown
 * Each debt is displayed as a visual card with stacked fields
 */

import React, { useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

export interface DebtItem {
  type: string;
  amount: number;
  rate?: number;
  monthly_payment?: number;
  duration_months?: number;
}

export interface DebtTypeOption {
  value: string;
  label: string;
}

interface DebtCardInputProps {
  items: DebtItem[];
  onChangeItems: (items: DebtItem[]) => void;
  typeOptions: DebtTypeOption[];
  maxLines?: number;
  autoRevealCount?: number; // Number of types to auto-reveal (default: 1 = no auto-reveal)
}

export default function DebtCardInput({
  items,
  onChangeItems,
  typeOptions,
  maxLines = 8,
  autoRevealCount = 1,
}: DebtCardInputProps) {
  // Track raw input strings for decimal fields (to preserve "1." while typing)
  const [rateInputs, setRateInputs] = React.useState<{ [key: number]: string }>({});
  const autoRevealTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoRevealTimeout.current) {
        clearTimeout(autoRevealTimeout.current);
      }
    };
  }, []);

  // Calculate total
  const total = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalMonthlyPayment = items.reduce((sum, item) => sum + (item.monthly_payment || 0), 0);

  // Add new debt
  const handleAddDebt = () => {
    if (items.length >= maxLines) return;

    // Find first unused type or use 'other'
    const usedTypes = items.map(item => item.type);
    const availableType = typeOptions.find(opt => !usedTypes.includes(opt.value))?.value || 'other';

    onChangeItems([...items, { type: availableType, amount: 0 }]);
  };

  // Remove debt
  const handleRemoveDebt = (index: number) => {
    if (items.length <= 1) return;
    const newItems = items.filter((_, i) => i !== index);
    onChangeItems(newItems);
  };

  // Update handlers
  const handleTypeChange = (index: number, newType: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], type: newType };
    onChangeItems(newItems);
  };

  const handleAmountChange = (index: number, value: string) => {
    const numValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], amount: numValue };
    onChangeItems(newItems);
  };

  const handleRateChange = (index: number, value: string) => {
    // Keep only valid decimal characters (digits and one decimal point)
    let cleanValue = value.replace(/[^0-9.,]/g, '');
    // Replace comma with dot for French decimal separator
    cleanValue = cleanValue.replace(',', '.');
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Store raw string to preserve "1." while typing
    setRateInputs(prev => ({ ...prev, [index]: cleanValue }));

    // Parse to number for state (will be 0 if just "." or empty)
    const numValue = parseFloat(cleanValue) || 0;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], rate: numValue };
    onChangeItems(newItems);
  };

  // Get display value for rate input (use raw input if available, otherwise from item)
  const getRateDisplayValue = (index: number, itemRate?: number): string => {
    if (rateInputs[index] !== undefined) {
      return rateInputs[index];
    }
    return itemRate ? itemRate.toString() : '';
  };

  const handleMonthlyPaymentChange = (index: number, value: string) => {
    const numValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], monthly_payment: numValue };
    onChangeItems(newItems);
  };

  const handleDurationChange = (index: number, value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, ''), 10) || 0;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], duration_months: numValue };
    onChangeItems(newItems);
  };

  // Auto-reveal next field when user leaves the last item with a value > 0
  const handleAmountBlur = (index: number) => {
    const item = items[index];
    const isLastItem = index === items.length - 1;

    if (
      isLastItem &&
      item.amount > 0 &&
      items.length < autoRevealCount &&
      items.length < maxLines
    ) {
      if (autoRevealTimeout.current) {
        clearTimeout(autoRevealTimeout.current);
      }
      autoRevealTimeout.current = setTimeout(() => {
        const usedTypes = items.map(i => i.type);
        const nextType = typeOptions.find(opt => !usedTypes.includes(opt.value));

        if (nextType) {
          onChangeItems([...items, { type: nextType.value, amount: 0 }]);
        }
      }, 300);
    }
  };

  // Format number for display
  const formatNumber = (num: number): string => {
    if (num === 0) return '';
    return num.toLocaleString('fr-FR');
  };

  // Get label for debt type
  const getTypeLabel = (type: string): string => {
    return typeOptions.find(opt => opt.value === type)?.label || type;
  };

  return (
    <View style={styles.container}>
      {items.map((item, index) => (
        <View key={index} style={styles.card}>
          {/* Card Header with Type Picker and Delete */}
          <View style={styles.cardHeader}>
            <View style={styles.pickerWrapper}>
              <Picker
                selectedValue={item.type}
                onValueChange={(value: string) => handleTypeChange(index, value)}
                style={styles.picker}
                itemStyle={styles.pickerItem}
              >
                {typeOptions.map((option) => (
                  <Picker.Item
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  />
                ))}
              </Picker>
            </View>
            {items.length > 1 && (
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={() => handleRemoveDebt(index)}
              >
                <Text style={styles.deleteButtonText}>✕</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Card Body with Fields */}
          <View style={styles.cardBody}>
            {/* Amount Field */}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Montant total</Text>
              <View style={styles.fieldInput}>
                <TextInput
                  style={styles.input}
                  value={formatNumber(item.amount)}
                  onChangeText={(value) => handleAmountChange(index, value)}
                  onBlur={() => handleAmountBlur(index)}
                  placeholder="0"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="numeric"
                />
                <Text style={styles.inputUnit}>€</Text>
              </View>
            </View>

            {/* Rate Field */}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Taux d'intérêt</Text>
              <View style={styles.fieldInput}>
                <TextInput
                  style={styles.input}
                  value={getRateDisplayValue(index, item.rate)}
                  onChangeText={(value) => handleRateChange(index, value)}
                  onBlur={() => {
                    // Clear raw input on blur to use formatted value
                    setRateInputs(prev => {
                      const newInputs = { ...prev };
                      delete newInputs[index];
                      return newInputs;
                    });
                  }}
                  placeholder="0"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="decimal-pad"
                />
                <Text style={styles.inputUnit}>%</Text>
              </View>
            </View>

            {/* Monthly Payment Field */}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Mensualité</Text>
              <View style={styles.fieldInput}>
                <TextInput
                  style={styles.input}
                  value={item.monthly_payment ? formatNumber(item.monthly_payment) : ''}
                  onChangeText={(value) => handleMonthlyPaymentChange(index, value)}
                  placeholder="0"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="numeric"
                />
                <Text style={styles.inputUnit}>€/mois</Text>
              </View>
            </View>

            {/* Duration Field */}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Durée restante</Text>
              <View style={styles.fieldInput}>
                <TextInput
                  style={styles.input}
                  value={item.duration_months ? item.duration_months.toString() : ''}
                  onChangeText={(value) => handleDurationChange(index, value)}
                  placeholder="0"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="numeric"
                />
                <Text style={styles.inputUnit}>mois</Text>
              </View>
            </View>
          </View>
        </View>
      ))}

      {/* Add Debt Button */}
      {items.length < maxLines && (
        <TouchableOpacity style={styles.addButton} onPress={handleAddDebt}>
          <Text style={styles.addButtonText}>+ Ajouter une dette</Text>
        </TouchableOpacity>
      )}

      {/* Total Summary */}
      <View style={styles.totalContainer}>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Total dettes</Text>
          <Text style={styles.totalValue}>
            {total.toLocaleString('fr-FR')} €
          </Text>
        </View>
        {totalMonthlyPayment > 0 && (
          <View style={styles.totalRow}>
            <Text style={styles.totalLabelSecondary}>Total mensualités</Text>
            <Text style={styles.totalValueSecondary}>
              {totalMonthlyPayment.toLocaleString('fr-FR')} €/mois
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  card: {
    backgroundColor: colors.background,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.md,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingRight: spacing.sm,
    paddingVertical: 6,
    paddingLeft: spacing.xs,
  },
  pickerWrapper: {
    flex: 1,
    height: 36,
    justifyContent: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  picker: {
    height: 36,
    width: '100%',
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingHorizontal: spacing.sm,
  },
  pickerItem: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  deleteButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.error + '15',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: spacing.sm,
  },
  deleteButtonText: {
    color: colors.error,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
  },
  cardBody: {
    padding: spacing.md,
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border + '50',
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    flex: 1,
  },
  fieldInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    minWidth: 140,
  },
  input: {
    flex: 1,
    fontSize: typography.fontSize.md,
    color: colors.text,
    textAlign: 'right',
    paddingVertical: 4,
    minWidth: 60,
  },
  inputUnit: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
    minWidth: 45,
  },
  addButton: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.primary + '15',
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary + '30',
    borderStyle: 'dashed',
  },
  addButtonText: {
    color: colors.primary,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
  totalContainer: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary + '30',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  totalLabel: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  totalValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
  },
  totalLabelSecondary: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  totalValueSecondary: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textSecondary,
  },
});
